import { useState } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Sparkles, Wand2, Loader2, X, ChevronDown, ChevronUp, RotateCcw, Image as ImageIcon } from 'lucide-react';

const COLORS = ['#6A806C', '#C5A059', '#0B192C'];
const ICONS = ['brain', 'wallet', 'heart', 'users', 'briefcase', 'activity', 'sparkles', 'trending-up', 'user', 'hand-heart'];

/**
 * Admin-only: draft a full Learn course (category + richly-structured lessons)
 * with AI, review/edit, then publish live. Mirrors the exact schema used across
 * the app so new courses look and behave like the built-in ones.
 */
export default function AICourseGenerator({ onPublished }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('form'); // 'form' | 'review'
  const [busy, setBusy] = useState(false);

  const [topic, setTopic] = useState('');
  const [numLessons, setNumLessons] = useState(5);
  const [premium, setPremium] = useState(false);
  const [notes, setNotes] = useState('');

  const [draft, setDraft] = useState(null); // { category, lessons }
  const [showJson, setShowJson] = useState(false);
  const [regenIdx, setRegenIdx] = useState(-1);
  const [coverBusy, setCoverBusy] = useState(false);
  const [redoAllBusy, setRedoAllBusy] = useState(false);

  const reset = () => { setStep('form'); setDraft(null); setTopic(''); setNotes(''); setNumLessons(5); setPremium(false); setShowJson(false); };
  const close = () => { if (busy) return; setOpen(false); reset(); };

  const generate = async () => {
    if (!topic.trim()) { toast.error('Enter a topic first'); return; }
    setBusy(true);
    try {
      const { data } = await http.post('/admin/courses/generate',
        { topic: topic.trim(), num_lessons: Number(numLessons), premium, notes: notes.trim() },
        { timeout: 180000 });
      setDraft(data);
      setStep('review');
      toast.success('Draft ready — review and publish');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed. Please try again.');
    } finally { setBusy(false); }
  };

  const publish = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const { data } = await http.post('/admin/courses/publish', draft, { timeout: 60000 });
      toast.success(`Published "${data.category?.name}" — ${data.lessons_saved} lessons live`);
      setOpen(false); reset();
      onPublished && onPublished();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Publish failed');
    } finally { setBusy(false); }
  };

  const setCat = (k, v) => setDraft(d => ({ ...d, category: { ...d.category, [k]: v } }));
  const setLesson = (i, k, v) => setDraft(d => ({ ...d, lessons: d.lessons.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));
  const syncJson = (txt) => { try { setDraft(JSON.parse(txt)); } catch { /* keep typing */ } };

  const regenLesson = async (i) => {
    if (!draft) return;
    const l = draft.lessons[i];
    setRegenIdx(i);
    try {
      const { data } = await http.post('/admin/courses/regenerate-lesson', {
        topic: (topic.trim() || draft.category?.name || ''),
        category_name: draft.category?.name || '',
        category_id: draft.category?.id || '',
        title: l.title || '', summary: l.summary || '',
        premium: !!draft.category?.premium, index: i, total: draft.lessons.length,
      }, { timeout: 120000 });
      setDraft(d => ({ ...d, lessons: d.lessons.map((x, idx) => idx === i ? data.lesson : x) }));
      toast.success('Lesson regenerated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Regenerate failed');
    } finally { setRegenIdx(-1); }
  };

  const genCover = async () => {
    if (!draft?.category) return;
    setCoverBusy(true);
    try {
      const { data } = await http.post('/admin/courses/generate-cover', {
        name: draft.category.name || '', description: draft.category.description || '',
        icon: draft.category.icon || 'sparkles', color: draft.category.color || '#6A806C',
      }, { timeout: 120000 });
      setCat('cover_image', data.cover_image);
      toast.success('Custom cover generated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cover generation failed');
    } finally { setCoverBusy(false); }
  };

  const redoAll = async () => {
    if (!draft?.lessons?.length) return;
    setRedoAllBusy(true);
    try {
      const results = await Promise.all(draft.lessons.map((l, i) =>
        http.post('/admin/courses/regenerate-lesson', {
          topic: (topic.trim() || draft.category?.name || ''),
          category_name: draft.category?.name || '', category_id: draft.category?.id || '',
          title: l.title || '', summary: l.summary || '',
          premium: !!draft.category?.premium, index: i, total: draft.lessons.length,
        }, { timeout: 150000 }).then(r => r.data.lesson).catch(() => l)
      ));
      setDraft(d => ({ ...d, lessons: results }));
      toast.success('All lessons regenerated');
    } catch (e) {
      toast.error('Some lessons could not be regenerated');
    } finally { setRedoAllBusy(false); }
  };

  return (
    <>
      <button
        data-testid="ai-course-generate-btn"
        onClick={() => setOpen(true)}
        className="text-xs bg-gold text-navy rounded-full px-4 py-2 inline-flex items-center gap-1.5 font-semibold hover:brightness-105 transition">
        <Sparkles size={13} /> Generate Course (AI)
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={close}>
          <div onClick={e => e.stopPropagation()} data-testid="ai-course-modal"
            className="w-full max-w-2xl bg-white rounded-3xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <p className="font-heading text-xl inline-flex items-center gap-2">
                <Wand2 size={18} className="text-gold" /> {step === 'form' ? 'Generate a Course' : 'Review & Publish'}
              </p>
              <button data-testid="ai-course-close" onClick={close}><X size={18} /></button>
            </div>

            {step === 'form' && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-navy/70">Course topic</label>
                  <textarea data-testid="ai-course-topic" value={topic} onChange={e => setTopic(e.target.value)} rows={2}
                    placeholder="e.g. Managing anger and frustration in a healthy way"
                    className="mt-1 w-full text-sm bg-[#F9F8F6] border border-navy/10 rounded-2xl p-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-xs font-medium text-navy/70"># of lessons</label>
                    <select data-testid="ai-course-count" value={numLessons} onChange={e => setNumLessons(e.target.value)}
                      className="mt-1 w-full text-sm bg-[#F9F8F6] border border-navy/10 rounded-2xl p-3 focus:outline-none focus:ring-2 focus:ring-gold">
                      {[3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n} lessons</option>)}
                    </select>
                  </div>
                  <div className="flex-1">
                    <label className="text-xs font-medium text-navy/70">Access</label>
                    <button data-testid="ai-course-premium" onClick={() => setPremium(p => !p)}
                      className={`mt-1 w-full text-sm rounded-2xl p-3 border transition ${premium ? 'bg-navy text-white border-navy' : 'bg-[#F9F8F6] text-navy border-navy/10'}`}>
                      {premium ? 'Premium (members)' : 'Free (everyone)'}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-navy/70">Notes to the AI (optional)</label>
                  <input data-testid="ai-course-notes" value={notes} onChange={e => setNotes(e.target.value)}
                    placeholder="Tone, audience, must-cover points…"
                    className="mt-1 w-full text-sm bg-[#F9F8F6] border border-navy/10 rounded-2xl p-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                </div>
                <button data-testid="ai-course-generate-submit" onClick={generate} disabled={busy}
                  className="w-full bg-gold text-navy rounded-full py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <><Loader2 size={16} className="animate-spin" /> Generating… (~30–60s)</> : <><Sparkles size={16} /> Generate</>}
                </button>
                <p className="text-[10px] text-navy/50 text-center">Uses your AI credits. You&apos;ll review everything before it goes live.</p>
              </div>
            )}

            {step === 'review' && draft && (
              <div className="space-y-4">
                <div className="bg-[#F9F8F6] rounded-2xl p-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="text-[10px] uppercase tracking-widest text-navy/50">Course name</label>
                      <input data-testid="ai-course-cat-name" value={draft.category?.name || ''} onChange={e => setCat('name', e.target.value)}
                        className="mt-1 w-full text-sm font-semibold bg-white border border-navy/10 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold" />
                    </div>
                    <div className="w-28">
                      <label className="text-[10px] uppercase tracking-widest text-navy/50">Icon</label>
                      <select value={draft.category?.icon || 'sparkles'} onChange={e => setCat('icon', e.target.value)}
                        className="mt-1 w-full text-sm bg-white border border-navy/10 rounded-xl px-2 py-2 focus:outline-none focus:ring-2 focus:ring-gold">
                        {ICONS.map(i => <option key={i} value={i}>{i}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-navy/50">Description</label>
                    <input data-testid="ai-course-cat-desc" value={draft.category?.description || ''} onChange={e => setCat('description', e.target.value)}
                      className="mt-1 w-full text-sm bg-white border border-navy/10 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gold" />
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      {COLORS.map(c => (
                        <button key={c} onClick={() => setCat('color', c)} title={c}
                          className={`w-6 h-6 rounded-full border-2 ${draft.category?.color === c ? 'border-navy' : 'border-transparent'}`}
                          style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <button onClick={() => setCat('premium', !draft.category?.premium)}
                      className={`text-xs rounded-full px-3 py-1.5 border ${draft.category?.premium ? 'bg-navy text-white border-navy' : 'bg-white text-navy border-navy/15'}`}>
                      {draft.category?.premium ? 'Premium' : 'Free'}
                    </button>
                  </div>
                  {/* Course cover — smart-matched by default; optional bespoke AI generation */}
                  <div className="flex items-center gap-3 pt-1">
                    {draft.category?.cover_image
                      ? <img src={draft.category.cover_image} alt="course cover" data-testid="ai-course-cover-preview"
                          className="w-24 h-14 rounded-lg object-cover border border-navy/10" />
                      : <div className="w-24 h-14 rounded-lg bg-navy/10 border border-navy/10" />}
                    <button data-testid="ai-course-gen-cover" onClick={genCover} disabled={coverBusy}
                      className="text-xs rounded-full px-3 py-2 border border-navy/15 inline-flex items-center gap-1.5 hover:bg-[#F9F8F6] disabled:opacity-60">
                      {coverBusy ? <><Loader2 size={12} className="animate-spin" /> Generating cover…</> : <><ImageIcon size={12} /> Generate custom AI cover</>}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-navy/60">Lessons ({(draft.lessons || []).length})</p>
                  <button data-testid="ai-course-redo-all" onClick={redoAll} disabled={redoAllBusy || regenIdx !== -1}
                    className="text-[11px] text-navy/60 inline-flex items-center gap-1 hover:text-navy disabled:opacity-50">
                    {redoAllBusy ? <><Loader2 size={12} className="animate-spin" /> Re-rolling all…</> : <><RotateCcw size={12} /> Redo all</>}
                  </button>
                </div>

                <div className="space-y-2" data-testid="ai-course-lessons">
                  {(draft.lessons || []).map((l, i) => (
                    <div key={l.id || i} className="border border-navy/10 rounded-2xl p-3">
                      <div className="flex items-center gap-2">
                        <span className="w-6 h-6 shrink-0 rounded-full bg-navy text-white text-[11px] flex items-center justify-center">{i + 1}</span>
                        <input value={l.title || ''} onChange={e => setLesson(i, 'title', e.target.value)}
                          className="flex-1 text-sm font-medium bg-transparent border-b border-transparent focus:border-navy/20 focus:outline-none py-1" />
                        <button data-testid={`ai-course-redo-${i}`} onClick={() => regenLesson(i)} disabled={regenIdx !== -1}
                          title="Re-roll this lesson"
                          className="shrink-0 text-[11px] text-navy/60 inline-flex items-center gap-1 hover:text-navy disabled:opacity-50">
                          {regenIdx === i ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Redo
                        </button>
                      </div>
                      <input value={l.summary || ''} onChange={e => setLesson(i, 'summary', e.target.value)}
                        className="mt-1 w-full text-xs text-navy/60 bg-transparent focus:outline-none" />
                      <p className="text-[10px] text-navy/40 mt-1">
                        {(l.sections || []).length} sections · {(l.ai_coach_prompts || []).length} coach prompts · {l.evidence_spotlight?.source ? 'evidence ✓' : 'no evidence'}
                      </p>
                    </div>
                  ))}
                </div>

                <div>
                  <button onClick={() => setShowJson(s => !s)} className="text-xs text-navy/60 inline-flex items-center gap-1">
                    {showJson ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Advanced: edit raw JSON
                  </button>
                  {showJson && (
                    <textarea data-testid="ai-course-json" defaultValue={JSON.stringify(draft, null, 2)} onChange={e => syncJson(e.target.value)} rows={12}
                      className="mt-2 w-full font-mono text-[11px] bg-[#F9F8F6] border border-navy/10 rounded-2xl p-3 focus:outline-none focus:ring-2 focus:ring-gold" />
                  )}
                </div>

                <div className="flex gap-2">
                  <button data-testid="ai-course-back" onClick={() => setStep('form')} disabled={busy}
                    className="flex-1 bg-beige text-navy rounded-full py-3 font-medium disabled:opacity-60">Back</button>
                  <button data-testid="ai-course-publish" onClick={publish} disabled={busy}
                    className="flex-[2] bg-navy text-white rounded-full py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                    {busy ? <><Loader2 size={16} className="animate-spin" /> Publishing…</> : 'Publish Course (live)'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
